import { api } from '@api'
import type { RealtimeChannel } from '@supabase/supabase-js'
import { createEffect, createSignal, onCleanup } from 'solid-js'
import { useService } from 'solid-services'

import { useAuth, userId } from '@stateApp'
import { useChannel } from './useChannel'

interface TypingPresence {
  userId: string
  username?: string
  isTyping: boolean
  updatedAt: string
}

export function usePresence() {
  const [typingUsers, setTypingUsers] = createSignal<string[]>([])

  const { username } = useService(useAuth)()
  const { activeChannelId } = useService(useChannel)()

  let presenceChannel: RealtimeChannel | null = null

  createEffect(() => {
    // 1. You must execute the signals with () to get their reactive values!
    const channelIdVal = activeChannelId()
    const currentUserIdVal = userId()

    console.log('[Presence Debug] Config evaluated:', { channelIdVal, currentUserIdVal })

    if (!channelIdVal || !currentUserIdVal) {
      console.log('[Presence Debug] Missing activeChannelId or currentUserId, aborting connection.')
      return
    }

    if (presenceChannel) {
      presenceChannel.unsubscribe()
    }

    // 2. DO NOT append the userId to the channel name!
    // Everyone in the same chat needs to subscribe to the exact same channel string.
    console.log(`[Presence Debug] Initializing channel: typing:${channelIdVal}`)
    presenceChannel = api().channel(`typing:${channelIdVal}`)

    presenceChannel
      .on('presence', { event: 'sync' }, () => {
        const state = presenceChannel?.presenceState<TypingPresence>() || {}

        const activeTypers = Object.values(state)
          .flat()
          .filter((user) => user.isTyping && user.userId !== currentUserIdVal)

        const uniqueTypersMap = new Map<string, string>()

        for (const user of activeTypers) {
          uniqueTypersMap.set(user.userId, user.username || 'someone')
        }

        const finalUsers = Array.from(uniqueTypersMap.values())
        setTypingUsers(finalUsers)
      })
      .subscribe()

    onCleanup(() => {
      if (presenceChannel) {
        presenceChannel.unsubscribe()
        presenceChannel = null
      }
    })
  })

  const startTyping = async () => {
    const currentUserId = userId()
    const currentUsername = username()

    if (!presenceChannel || !currentUserId) return

    await presenceChannel.track({
      userId: currentUserId,
      username: currentUsername,
      isTyping: true,
      updatedAt: new Date().toISOString(),
    } satisfies TypingPresence)
  }

  const stopTyping = async () => {
    const currentUserId = userId()
    const currentUsername = username()

    if (!presenceChannel || !currentUserId) return

    await presenceChannel.track({
      userId: currentUserId,
      username: currentUsername,
      isTyping: false,
      updatedAt: new Date().toISOString(),
    } satisfies TypingPresence)
  }

  return {
    typingUsers,
    startTyping,
    stopTyping,
  }
}
